<template>
  <main class="container">
    <section id="configurator">
      <header class="header">
        <h1>Simulation tarifaire</h1>
        <p class="subtitle">Estimez votre offre Mashup Web Social</p>

        <!-- Period selector -->
        <PeriodSelector
          :selected-period="pricingState.period"
          @period-change="onPeriodChange"
        />
      </header>

      <!-- Slider controls -->
      <div class="sliders-container">
        <ControlSlider
          v-for="control in sliderControls"
          :key="control.key"
          :label="control.label"
          :icon="control.icon"
          :value="pricingState[control.key]"
          :scale-name="control.scale"
          :unique-id="control.key"
          @input="onValueChange(control.key, $event)"
        />
      </div>
    </section>

    <!-- Result card -->
    <ResultCard :result="selectedPlan" :environment="environment" />
  </main>
</template>

<script>
import { usePricingAdvanced } from "@composables/usePricingAdvanced"
import { usePricingBasic } from "@composables/usePricingBasic"
import {
  defaultConfig,
  INITIAL_VALUES,
  PERIODS,
  PRICING_MODES,
  ENVIRONMENTS,
} from "@config/pricingConfig"
import ControlSlider from "./ControlSlider.vue"
import PeriodSelector from "./PeriodSelector.vue"
import ResultCard from "./ResultCard.vue"

import networkIcon from "@/assets/img/network-icon.svg"
import sourceIcon from "@/assets/img/source-icon.svg"
import usersIcon from "@/assets/img/users-icon.svg"

export default {
  name: "PricingCalculator",

  components: {
    PeriodSelector,
    ControlSlider,
    ResultCard,
  },

  props: {
    // Allow switching logic mode via props
    mode: {
      type: String,
      default: defaultConfig.mode,
      validator: (value) => Object.values(PRICING_MODES).includes(value),
    },
    // Allow switching environment (chat platform)
    environment: {
      type: String,
      default: defaultConfig.environment,
      validator: (value) => Object.values(ENVIRONMENTS).includes(value),
    },
  },

  data() {
    return {
      pricingState: {
        period: PERIODS.ONE_YEAR, // Default period
        socialAccounts: 10, // Will be adjusted in mounted()
        users: 10,
        sources: 10,
      },
      sliderControls: [
        {
          key: "socialAccounts",
          label: "Nombre de comptes sociaux (pages)",
          icon: networkIcon,
          scale: "social",
        },
        {
          key: "users",
          label: "Nombre d'utilisateurs",
          icon: usersIcon,
          scale: "users",
        },
        {
          key: "sources",
          label: "Nombre de sources",
          icon: sourceIcon,
          scale: "sources",
        },
      ],
    }
  },

  computed: {
    pricingMode() {
      return this.mode
    },

    // Dynamic switch between pricing logics
    pricingCalculator() {
      return this.pricingMode === PRICING_MODES.ADVANCED
        ? usePricingAdvanced()
        : usePricingBasic()
    },

    selectedPlan() {
      return this.pricingCalculator.calculatePlan(this.pricingState)
    },
  },

  methods: {
    onPeriodChange(period) {
      this.pricingState.period = period
    },

    onValueChange(key, value) {
      this.pricingState[key] = value
    },

    // Method to switch pricing mode (for future testing)
    switchPricingMode(newMode) {
      if (Object.values(PRICING_MODES).includes(newMode)) {
        // Adjust initial values according to mode
        const initialValues = INITIAL_VALUES[newMode]
        Object.keys(initialValues).forEach((key) => {
          this.pricingState[key] = initialValues[key]
        })

        this.$emit("mode-change", newMode)
      }
    },
  },

  // Adjust initial values according to mode on mount
  mounted() {
    const initialValues = INITIAL_VALUES[this.pricingMode]
    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        this.pricingState[key] = initialValues[key]
      })
    }
  },

  // Watch for mode changes via props
  watch: {
    mode: {
      handler(newMode) {
        const initialValues = INITIAL_VALUES[newMode]
        if (initialValues) {
          Object.keys(initialValues).forEach((key) => {
            this.state[key] = initialValues[key]
          })
        }
      },
      immediate: false,
    },
  },
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  align-items: center;
  gap: 170px; // Adapted gap for Webflow
  max-width: 1200px;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 960px) {
    flex-direction: column;
    gap: 60px; // Adapted gap for Webflow
  }
}

#configurator {
  flex: 1;
  min-width: 300px;

  .header {
    width: fit-content;

    @media (max-width: 960px) {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    @media (max-width: 400px) {
      h1 {
        font-size: 34px;
      }
      .subtitle {
        font-size: 18px;
      }
    }
  }

  h1 {
    color: var(--primary-text-color);
    margin-bottom: 5px;
    margin-top: 0px;
    font-size: 40px;
    font-weight: 700;
  }

  .subtitle {
    color: var(--secondary-text-color);
    margin-top: 0;
    margin-bottom: 40px;
    font-size: 20px;
    font-weight: 400;
  }
}

.sliders-container {
  margin-top: 40px;

  > :not(:last-child) {
    margin-bottom: 40px;
  }
}
</style>
